// src/components/profiles/Member/components/testimonies/audio/AudioTestimonies.tsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../../../redux/storeTypes";
import { useRouter } from "next/navigation";

import { Plus, AudioLines, MoveRight, Upload, Mic, CloudUpload, Check, X, Trash2, Headphones, Play } from "lucide-react";
import {
  fetchSummary,
  fetchDetailBySlug,
  createTestimony,
  updateTestimonyBySlug,
  deleteTestimonyBySlug,
  resetOpStatus,
} from "../../../../../../redux/ugc/testimoniesSlice";
import {
  selectSummary,
  selectSummaryStatus,
  selectDetailBySlug,
  selectDetailStatusBySlug,
  selectOpStatus,
} from "../../../../../../redux/selectors/ugc/testimoniesSelectors";
import { normalizeBaseMime, normalizeUploadFile } from "../../../../../../utils/mediaTools/mimeUtils";
import { validateBundle, type Tier } from "../../../../../../utils/validators/mediaValidator";
import type { TestimonyDTO } from "../../../../../../types/ugc/testimonyTypes";
import { useAlertMessage } from "../../../../../../hooks/common/useAlertMessage";
import { AudioRecorder, ActionMenu, Modal, InlineStatus, ProgressCircle, VideoCardSkeleton } from "../../../../../../components/common";
import { UGC_ENDPOINTS } from "../../../../../../services/ugc/endpoint/endpoints";
import { makeVoiceUrl } from "../../../../../../utils/navigation/contentNavigation";

import MediaConversionPanel from "../../../../../../components/mediaConversion/MediaConversionPanel/MediaConversionPanel";
import { useMediaConversionJobs } from "../../../../../../hooks/mediaConversion/useMediaConversionJobs";

import "./AudioTestimonies.css";


// ----------------------------------------------
const AUDIO_TIER: Tier = "M";
const MAX_AUDIO_DURATION_S = Number(process.env.NEXT_PUBLIC_MEDIA_AUDIO_MAX_DURATION_S ?? 15 * 60);

/** Prepare a single audio file for sending (coerce + normalize + validate). */
async function prepareAudioForSend(input: File | null): Promise<{ file?: File; error?: string }> {
  if (!input) return { error: "Please choose a file." };

  // Coerce video/webm|video/ogg → audio/webm|audio/ogg for audio-only recordings
  const base = normalizeBaseMime(input.type);
  const isVideoLabeledAudio =
    base === "video/webm" ||
    base === "video/ogg" ||
    base === "video/mp4" ||
    base === "video/quicktime";

  let coerced: File = input;

  if (isVideoLabeledAudio) {
    // map to audio/*
    const targetMime =
      base === "video/webm" ? "audio/webm" :
        base === "video/ogg" ? "audio/ogg" :
    /* mp4|mov */                "audio/mp4";

    // ✅ build File from original bytes (no arrayBuffer), only fix type & name
    const newName = input.name.replace(/\.(webm|ogg|mp4|mov)$/i, (m) =>
      /mp4|mov/i.test(m) ? ".m4a" : m.toLowerCase()
    );
    coerced = new File([input], newName, { type: targetMime });
  }

  const normalized = normalizeUploadFile(coerced, "audio");

  // One-pass validation (size/format + optional duration; duration skips if undetectable)
  const bundle = await validateBundle(
    {
      audio: {
        file: normalized,
        kind: "audio",
        tier: AUDIO_TIER,
        extra: { maxDurationSec: MAX_AUDIO_DURATION_S },
      },
    },
    { useEnv: true }
  );

  if (bundle.ok) return { file: normalized };

  const firstErr = Object.values(bundle.errors)[0];
  return { error: firstErr?.message ?? "Audio validation failed." };
}


interface Props {
  isActive?: boolean;
}

const AudioTestimonies: React.FC<Props> = ({ isActive = true }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { message, showMessage, clearMessage } = useAlertMessage();
  const router = useRouter();

  // --- Global state
  const summary = useSelector(selectSummary);
  const summaryStatus = useSelector(selectSummaryStatus); // "idle" | "loading" | "succeeded" | "failed"

  const audioSummary = summary?.audio ?? { exists: false };
  const audioSlug = audioSummary.exists ? audioSummary.slug : undefined;

  const audioDetail = useSelector(
    audioSlug ? selectDetailBySlug(audioSlug) : () => undefined
  ) as TestimonyDTO | undefined;

  const detailStatus = useSelector(
    audioSlug ? selectDetailStatusBySlug(audioSlug) : () => "idle"
  ); // "idle" | "loading" | "succeeded" | "failed"

  // --- Local UI
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createMode, setCreateMode] = useState<"upload" | "record">("upload");

  const [audioFile, setAudioFile] = useState<File | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // --- Derived flags
  const baseLoading = summaryStatus === "loading";
  const detailLoading = !!audioSlug && detailStatus === "loading";
  const isLoading = baseLoading || detailLoading;

  // Only consider empty when not loading
  const isEmpty = !isLoading && audioSummary.exists === false;


  // --- Op status
  const createKey = "create:audio";
  const updatingKey = `update:audio:${audioSlug ?? "x"}`;
  const deletingKey = `delete:audio:${audioSlug ?? "x"}`;

  const createStatus = useSelector(selectOpStatus(createKey));   // "idle" | "loading" | "succeeded" | "failed"
  const updateStatus = useSelector(selectOpStatus(updatingKey));
  const deleteStatus = useSelector(selectOpStatus(deletingKey));

  const replaceInputRef = useRef<HTMLInputElement | null>(null);

  // Modal -----------------------------------------------------------
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteAck, setDeleteAck] = useState(false);


  // --- Upload progress & abort
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [uploadKind, setUploadKind] = useState<"create" | "replace" | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const isUploading = uploadPct !== null;

  const mediaTarget = audioSlug
    ? {
      by: "slug" as const,
      appLabel: "posts",
      model: "testimony",
      slug: audioSlug,
    }
    : null;

  const { jobs: conversionJobs } = useMediaConversionJobs(mediaTarget, {
    enabled: Boolean(mediaTarget) && isActive,
    fieldName: "audio",
  });

  const hasConversionJobs = conversionJobs.length > 0;

  const hasActiveConversion = conversionJobs.some(
    (j) => j.status === "queued" || j.status === "processing"
  );

  const isConverting = hasActiveConversion;


  // Abort on unmount -----------------------------------------------
  useEffect(() => {
    // cleanup on unmount
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  // Upload helpers -----------------------------------------------
  const startNewAbortController = () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    return ctrl;
  };

  // Abort upload ------------------------------------------------
  const handleAbortUpload = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setUploadPct(null);
    setUploadKind(null);
    showMessage("Upload canceled.", "warning");
  };


  // --- Fetch summary on mount (robust)
  useEffect(() => {
    if (summaryStatus === "idle" || summaryStatus === "failed") {
      dispatch(fetchSummary());
    }
  }, [dispatch, summaryStatus]);

  // --- Fetch detail when slug appears
  useEffect(() => {
    if (!audioSlug) return;
    if (!hasConversionJobs) return;
    if (hasActiveConversion) return;
    if (!audioDetail && detailStatus !== "loading") {
      dispatch(fetchDetailBySlug(audioSlug));
    }
  }, [
    dispatch,
    audioSlug,
    audioDetail,
    detailStatus,
    hasConversionJobs,
    hasActiveConversion,
  ]);


  // ------------------------------------------------------------------
  const openDeleteModal = () => {
    setDeleteAck(false);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!audioSlug) return;
    try {
      await dispatch(deleteTestimonyBySlug({ type: "audio", slug: audioSlug })).unwrap();
      showMessage("Audio testimony deleted.", "success");
      setShowDeleteModal(false);
      dispatch(fetchSummary());
      dispatch(resetOpStatus(deletingKey));
    } catch (e: any) {
      showMessage(e?.message || "Failed to delete.", "error");
    }
  };


  // -----------------------------------------------------------------------------
  // doCreate
  // -----------------------------------------------------------------------------
  const doCreate = async (inputFile: File) => {
    // Prepare (coerce → normalize → validate)
    const { file, error } = await prepareAudioForSend(inputFile);
    if (error) {
      showMessage(error, "error");
      return;
    }

    const fd = new FormData();
    fd.append("type", "audio");
    fd.append("audio", file!, file!.name || "recording.webm");

    const ctrl = startNewAbortController();
    setUploadKind("create");
    setUploadPct(0);

    try {
      const res = await dispatch(
        createTestimony({
          type: "audio",
          payload: fd,
          onUploadProgress: setUploadPct,
          signal: ctrl.signal,
        })
      ).unwrap();

      // success → cleanup & refresh
      setShowCreateForm(false);
      setCreateMode("upload");
      setAudioFile(null);
      showMessage("Audio testimony created.", "success");
      // if (res?.slug) dispatch(fetchDetailBySlug(res.slug));
      dispatch(resetOpStatus(createKey));
    } catch (e: any) {
      // --- 409: already exists => replace flow ---
      const status = e?.response?.status;
      const existingSlug = e?.response?.data?.existing_slug;
      const data = e?.response?.data;

      if (status === 409 && existingSlug) {
        const ok = window.confirm("You already have an audio testimony. Replace the existing one?");
        if (!ok) {
          showMessage("Replace canceled.", "info");
        } else {
          const fd2 = new FormData();
          fd2.append("audio", file!, file!.name);

          const ctrl2 = startNewAbortController();
          setUploadKind("replace");
          setUploadPct(0);

          try {
            const rep = await dispatch(
              updateTestimonyBySlug({
                type: "audio",
                slug: existingSlug,
                payload: fd2,
                onUploadProgress: setUploadPct,
                signal: ctrl2.signal,
              })
            ).unwrap();

            setShowCreateForm(false);
            setCreateMode("upload");
            setAudioFile(null);
            showMessage("Audio testimony replaced.", "success");
            // if (rep?.slug) dispatch(fetchDetailBySlug(rep.slug));
            dispatch(resetOpStatus(updatingKey.replace(":x", `:${existingSlug}`)));
          } catch (uErr: any) {
            showMessage(uErr?.message || "Failed to replace audio testimony.", "error");
          }
        }
      } else {
        showMessage(
          (typeof data === "string" && data) ||
          data?.message ||
          e?.message ||
          "Failed to create audio testimony.",
          "error"
        );
      }
    } finally {
      setUploadPct(null);
      setUploadKind(null);
      abortRef.current = null;
    }
  };


  // -----------------------------------------------------------------------------
  const handleCreateUpload = async () => {
    if (!audioFile) {
      showMessage("Please choose an audio file.", "error");
      return;
    }
    await doCreate(audioFile);
  };


  // -----------------------------------------------------------------------------
  // handleCreateRecorded
  // -----------------------------------------------------------------------------
  const handleCreateRecorded = async (file: File) => {
    try {
      // Fast emptiness guard (size + underlying buffer)
      const sizeByBuffer = await file.arrayBuffer().then((b) => (b as ArrayBuffer).byteLength);
      if (!file || file.size === 0 || sizeByBuffer === 0) {
        showMessage("Recording failed: empty audio. Please record again.", "error");
        return;
      }

      // Prepare (coerce → normalize → validate)
      const { file: ready, error } = await prepareAudioForSend(file);
      if (error) {
        showMessage(error, "error");
        return;
      }

      // Send
      await doCreate(ready!);
    } catch (e: any) {
      showMessage(e?.message || "Failed to prepare recording.", "error");
    }
  };

  // -----------------------------------------------------------------------------
  // handleReplaceAudio
  // -----------------------------------------------------------------------------
  const handleReplaceAudio = async (file: File | null) => {
    if (!audioSlug) return;
    if (replaceInputRef.current) replaceInputRef.current.value = "";

    // Prepare (coerce → normalize → validate)
    const { file: ready, error } = await prepareAudioForSend(file);
    if (error) {
      showMessage(error, "error");
      return;
    }

    const fd = new FormData();
    fd.append("audio", ready!, ready!.name);

    const ctrl = startNewAbortController();
    setUploadKind("replace");
    setUploadPct(0);

    try {
      const res = await dispatch(
        updateTestimonyBySlug({
          type: "audio",
          slug: audioSlug,
          payload: fd,
          onUploadProgress: setUploadPct,
          signal: ctrl.signal,
        })
      ).unwrap();

      showMessage("Audio file replaced. Converting…", "success");
      // if (res?.slug) dispatch(fetchDetailBySlug(res.slug));
      dispatch(resetOpStatus(updatingKey));
    } catch (e: any) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
        showMessage("Upload canceled.", "warning");
      } else {
        showMessage(e?.message || "Failed to replace audio.", "error");
      }
    } finally {
      setUploadPct(null);
      setUploadKind(null);
      abortRef.current = null;
    }
  };


  // Renders -------------------------------------------------------------
  const renderCreateForm = () => (
    <div className="t-audio-card">
      <div className="t-audio-card-header">
        <div className="mode-switch">
          <button
            className={`mode-btn ${createMode === "upload" ? "active" : ""}`}
            onClick={() => !isUploading && setCreateMode("upload")}
            disabled={isUploading}
          >
            <Upload size={18} />
          </button>
          <button
            className={`mode-btn ${createMode === "record" ? "active" : ""}`}
            onClick={() => !isUploading && setCreateMode("record")}
            disabled={isUploading}
          >
            <Mic size={18} />
          </button>
        </div>
      </div>

      <div className="t-audio-card-body">
        {createMode === "upload" ? (
          <>
            {/* Minimal upload trigger with CloudUpload */}
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/mpeg,audio/mp3,audio/x-m4a,audio/m4a,audio/mp4,audio/wav,audio/x-wav,audio/webm,audio/ogg"
              className="hidden-file-input"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setAudioFile(f ? normalizeUploadFile(f, "audio") : null);
              }}
            />

            {audioFile ? (
              <div className="upload-filename" title={audioFile.name}>
                {audioFile.name}
              </div>
            ) : (
              <button
                type="button"
                className="icon-upload-btn"
                aria-label="Upload audio file"
                title="Upload audio file"
                onClick={() => fileInputRef.current?.click()}
              >
                <CloudUpload size={28} />
                <span>Speak Your Testimony</span>
              </button>
            )}
          </>
        ) : (
          <AudioRecorder
            disabled={createStatus === "loading"}
            onCancel={() => setShowCreateForm(false)}
            onSave={handleCreateRecorded}
          />
        )}
      </div>

      {isUploading && uploadKind === "create" && (
        <div className="progress-overlay">
          <ProgressCircle
            value={uploadPct ?? 0}
            onCancel={handleAbortUpload}
            cancelable
            label="Uploading"
            layout="overlay"
            size={68}
            stroke={6}
            rounded
            glow
            backdrop
            backdropOpacity={0.7}
            backdropBlur={3}
          />
        </div>
      )}


      {createMode === "upload" && audioFile && (
        <div className="t-audio-card-actions">
          <X
            size={32}
            className="x-icon-action"
            onClick={() => {
              setShowCreateForm(false);
              setAudioFile(null);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
            role="button"
            aria-label="Cancel"
          />
          <Check
            size={32}
            className="check-icon-action"
            onClick={!isUploading ? handleCreateUpload : undefined}
            role="button"
            aria-disabled={isUploading}
            aria-label="Confirm create"
          />
        </div>
      )}
    </div>
  );

  // ----------------------------------------------------------------------
  const renderEmpty = () => (
    <div
      className="audio-empty"
      onClick={() => setShowCreateForm(true)}
    >
      <div className="audio-empty-action">
        <Plus className="plus-btn" size={32} strokeWidth={3} />
        <MoveRight className="move-icone" size={32} />
        <AudioLines className="audio-icon" size={36} strokeWidth={1} />
      </div>
      <p className="empty-hint">
        Your voice matters!
        Share your audio testimony and let your story inspire others in TownLIT.
      </p>
    </div>
  );

  // ----------------------------------------------------------------------
  const renderConverting = () => {
    if (!mediaTarget) return null;

    return (
      <div className="t-audio-card t-audio-card--processing">
        <div className="t-audio-card-body">
          <MediaConversionPanel
            target={mediaTarget}
            fieldName="audio"
            compact
            title="Processing audio"
            density="mini"
          />
        </div>
      </div>
    );
  };


  // ----------------------------------------------------------------------
  const renderReady = () => {
    if (!audioDetail) return null;
    const audioSrc = audioDetail.audio_signed_url ?? null;
    const replaceDisabled = updateStatus === "loading" || isConverting || isUploading;
    const deleteDisabled = deleteStatus === "loading" || isUploading;

    const LISTEN_ENDPOINT = UGC_ENDPOINTS.testimonies.root;

    const listenUrl = audioDetail?.slug
      ? makeVoiceUrl(audioDetail.slug, {
        endpoint: LISTEN_ENDPOINT,
        scope: "flat",
        title: audioDetail.title || undefined,
        focus: "profile",
      })
      : null;

    const actionOptions = [
      {
        label: replaceDisabled ? "Updating…" : "Replace audio",
        icon: <Upload size={16} />,
        disabled: replaceDisabled,
        onClick: () => replaceInputRef.current?.click(),
      },
      {
        label: deleteDisabled ? "Deleting…" : "Delete",
        icon: <Trash2 size={16} />,
        disabled: deleteDisabled,
        danger: true,
        onClick: openDeleteModal,
      },
    ];
    return (
      <div
        className="t-audio-card clickable-card tl-indicator-aurora"
        onClick={() => {
          if (!isUploading && !isConverting && listenUrl) router.push(listenUrl);
        }}
      >
        <div className="t-audio-card-header">
          <div
            className="audio-card-actions-top-right"
            onClick={(e) => e.stopPropagation()}
          >
            <ActionMenu options={actionOptions} iconSize={32} position="bottom-right" color="primary" />
          </div>
        </div>

        <div className="t-audio-card-body">
          <div className="media-row">
            <div className="player-wrap">
              {(() => {
                const showUploading = isUploading && uploadKind === "replace";
                const showConverting = !isUploading && isConverting;
                const showPlayer = !!audioSrc;
                const showSkeleton =
                  detailStatus === "loading" && !showUploading && !showConverting && !showPlayer;

                return (
                  <>
                    {/* Player when signed URL is ready */}
                    <div className="player-layer-wrap">
                      {/* Player (lower layer) */}
                      {showPlayer && listenUrl && (
                        <div
                          className="audio-preview-card"
                          onClick={() => router.push(listenUrl)}
                        >
                          <div className="audio-preview-icon">
                            <Headphones size={38} strokeWidth={1.5} />
                            {/* <Play size={38} strokeWidth={1.5} /> */}
                          </div>

                          <div className="audio-preview-label">
                            Tap to listen
                          </div>
                        </div>
                      )}

                      {/* CLICKABLE OVERLAY (top) → go to content page */}
                      {!isUploading && !isConverting && listenUrl && (
                        <div
                          className="player-click-overlay"
                          onClick={() => router.push(listenUrl)}
                        />
                      )}
                    </div>

                    {/* Replace upload progress (overlay) */}
                    {showUploading && (
                      <div className="progress-overlay">
                        <ProgressCircle
                          value={uploadPct ?? 0}
                          onCancel={handleAbortUpload}
                          cancelable
                          label="Uploading"
                          layout="overlay"
                          size={64}
                          stroke={2}
                          backdrop
                          backdropOpacity={0.7}
                          backdropBlur={3}
                        />
                      </div>
                    )}

                    {/* Skeleton only for actual fetch */}
                    {showSkeleton && (
                      <VideoCardSkeleton variant="ultrawide" className="t-audio-skel-overlay" />
                    )}
                  </>
                );
              })()}

              {/* Hidden file input for "Replace audio" */}
              <input
                ref={replaceInputRef}
                type="file"
                accept="audio/mpeg,audio/mp3,audio/x-m4a,audio/m4a,audio/mp4,audio/wav,audio/x-wav,audio/webm,audio/ogg"
                className="hidden-file-input"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  handleReplaceAudio(f ? normalizeUploadFile(f, "audio") : null);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );

  };

  return (
    <div className="t-audio-wrap"> 
      {/* Loading skeleton */}
      {isLoading && (
        <VideoCardSkeleton
          variant="ultrawide"
          standalone
          showTitle
          className="t-audio-skel"
        />
      )}

      {!isLoading && (
        <>
          {isEmpty && !showCreateForm && renderEmpty()}
          {isEmpty && showCreateForm && renderCreateForm()}
          {!isEmpty && hasActiveConversion && renderConverting()}
          {!isEmpty && !hasActiveConversion && renderReady()}
        </>
      )}

      {/* Alerts */}
      {message && (
        <InlineStatus
          kind={
            message.type === "error" ? "error" :
              message.type === "warning" ? "warning" :
                message.type === "success" ? "success" : "info"
          }
          autoHideMs={1600}
          onHide={clearMessage}
          position="top-left"
          title={message.text || undefined}
          size={26}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          if (deleteStatus !== "loading") setShowDeleteModal(false);
        }}
        title="Delete audio testimony?"
        message={
          <div>
            <p>
              This action will permanently delete your audio testimony from TownLIT.
              You can upload a new one later, but this file cannot be recovered.
            </p>
            <label className="confirm-row" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
              <input
                type="checkbox"
                checked={deleteAck}
                onChange={(e) => setDeleteAck(e.target.checked)}
              />
              <span>I understand this action cannot be undone.</span>
            </label>
          </div>
        }
        confirmText={deleteStatus === "loading" ? "Deleting…" : "Delete"}
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        disableBackdropClick={deleteStatus === "loading"}
        isLoading={deleteStatus === "loading"}
        confirmVariant="danger"
        confirmDisabled={!deleteAck}
      />

    </div>
  );
};

export default AudioTestimonies;
